import api from "lib/axios";
import type { ApiEnvelope, PaginatedResult } from "services/apiTypes";
import { unwrapData, unwrapList } from "services/apiTypes";

export type CourseAnnouncement = {
  id: number;
  course_id?: number;
  title: string;
  content: string;
  image_url?: string | null;
  attachments?: AnnouncementAttachment[];
  is_pinned?: boolean;
  is_published?: boolean;
  published_at?: string | null;
  created_at?: string;
  updated_at?: string;
};

export type AnnouncementAttachment = {
  key: string;
  url?: string | null;
  name?: string | null;
  content_type?: string | null;
  size?: number | null;
};

export type AnnouncementUpsertBody = {
  title: string;
  content: string;
  attachments?: AnnouncementAttachment[];
  is_pinned?: boolean;
  is_published?: boolean;
};

export const instructorCourseAnnouncementsService = {
  async list(courseId: number | string, params: Record<string, unknown> = {}) {
    const res = await api.get<ApiEnvelope<PaginatedResult<CourseAnnouncement>>>(
      `/instructors/courses/${courseId}/announcements`,
      { params, meta: { toastSuccess: false } }
    );
    return unwrapList(res.data);
  },

  async get(courseId: number | string, announcementId: number | string) {
    const res = await api.get<ApiEnvelope<{ data: CourseAnnouncement }>>(
      `/instructors/courses/${courseId}/announcements/${announcementId}`,
      { meta: { toastSuccess: false } }
    );
    return unwrapData(res.data);
  },

  async create(courseId: number | string, body: AnnouncementUpsertBody) {
    const res = await api.post<ApiEnvelope<{ data: CourseAnnouncement }>>(
      `/instructors/courses/${courseId}/announcements`,
      body
    );
    return unwrapData(res.data);
  },

  async update(courseId: number | string, announcementId: number | string, body: AnnouncementUpsertBody) {
    const res = await api.put<ApiEnvelope<{ data: CourseAnnouncement }>>(
      `/instructors/courses/${courseId}/announcements/${announcementId}`,
      body
    );
    return unwrapData(res.data);
  },

  async remove(courseId: number | string, announcementId: number | string) {
    const res = await api.delete<ApiEnvelope<unknown>>(`/instructors/courses/${courseId}/announcements/${announcementId}`);
    return { message: (res.data as any)?.message ?? "Deleted." };
  },

  async setPinned(courseId: number | string, announcementId: number | string, isPinned: boolean) {
    const res = await api.patch<ApiEnvelope<{ data: CourseAnnouncement }>>(
      `/instructors/courses/${courseId}/announcements/${announcementId}/pin`,
      { is_pinned: isPinned }
    );
    return unwrapData(res.data);
  },

  async setPublished(courseId: number | string, announcementId: number | string, isPublished: boolean) {
    const res = await api.patch<ApiEnvelope<{ data: CourseAnnouncement }>>(
      `/instructors/courses/${courseId}/announcements/${announcementId}/publish`,
      { is_published: isPublished }
    );
    return unwrapData(res.data);
  },
};

